import { ToastType } from "@/components/common/toast/Toast";
import { useToast } from "@/components/common/toast/ToastProvider";
import { formatTimeDayMonthYear, formatTimeWeekDayMonthYear } from "@/components/table-ant/table-common";
import ViewMoreSection from "@/components/view_more_section";
import { ROUTES } from "@/constants";
import { StatusId, stringEmDash } from "@/constants/app_constants";
import { Images } from "@/constants/images";
import {
  START_DATE,
  SOMETHING_WENT_WRONG,
  DUE_DATE,
  JOB_LOCATION,
  DRAWING,
  READY_QTY,
  DELIVERED_QTY,
  DELIVERY_DOCKET,
  VIEW_JOB_ITEMS
} from "@/constants/strings";
import { THEME } from "@/constants/theme";
import { commonService } from "@/services/commonService";
import { MenuItem } from "@/stores/sidebarStore";
import { IMajorProjectJobDetails } from "@/types/majorProjects";
import { statusColors } from "@/utils/statusUtils";
import { useMutation } from "@tanstack/react-query";
import { Button } from "antd";
import { Link } from "react-router-dom";
import { isIOS, isIOS13, isIPad13, isTablet } from "react-device-detect";
import BreakLine from "@/components/common/break_line";
import React from "react";

interface ViewJobsListDetailItemCard {
  item: IMajorProjectJobDetails;
  handlePrintDeliveryDocket: (item: string) => void;
}

const ViewJobsListDetailItemCard: React.FC<ViewJobsListDetailItemCard> = ({ item, handlePrintDeliveryDocket }) => {
  const { showToast } = useToast();
  const userAgent = navigator.userAgent.toLowerCase();
  const isIPad = /iPad|Macintosh/i.test(userAgent) && /Safari/i.test(userAgent) && !/Chrome|Android/i.test(userAgent);

  // iOS only open view mode once trigger event directly inside button
  const usePrintDeliveryDocketIOS = useMutation<any, Error, string>({
    mutationFn: deliveryDocketNo =>
      commonService.printDeliveryDocketIOS({
        deliveryDocketNo: deliveryDocketNo
      })
  });

  return (
    <div className="border-1 rounded-md mb-4 w-full ml-3 mr-3" style={{ borderColor: statusColors[item.status ?? StatusId.inProduction] }}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg mb-2 text-gray-900 pt-3 pl-3 pr-3">{item.jobCodeAndPart}</h3>
        <Link
          to={ROUTES.VIEW_JOB_ITEM_DETAIL + `?tab=${MenuItem.majorProject}`}
          state={{
            id: item.id,
            name: item.projectName,
            jobCodeAndPart: item.jobCodeAndPart,
            jobLocation: item.jobLocation,
            drawing: item.drawingNumber
          }}
          className="!text-orange-500 !font-semibold !text-sm !pr-4 !cursor-pointer"
        >
          {VIEW_JOB_ITEMS}
        </Link>
      </div>
      <div className="pl-3 pr-3">
        <BreakLine />
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 gap-y-3 text-sm p-3">
        <div className="w-full col-span-1">
          <div className="text-gray-700 text-xs">{START_DATE}</div>
          <div className="font-semibold text-sm text-gray-900 mt-[6px]">{formatTimeDayMonthYear(item.importDate)}</div>
        </div>
        <div>
          <div className="text-gray-700 text-xs">{DUE_DATE}</div>
          <div className="font-semibold text-sm text-gray-900 mt-[6px]">{formatTimeWeekDayMonthYear(item.jobDueDate)}</div>
        </div>
        <div>
          <div className="text-gray-700 text-xs">{JOB_LOCATION}</div>
          <div className="font-semibold text-sm text-gray-900 mt-[6px]">{item.jobLocation}</div>
        </div>
        <div>
          <div className="text-gray-700 text-xs">{DRAWING}</div>
          <div className="font-semibold text-sm text-gray-900 mt-[6px]">{item.drawingNumber}</div>
        </div>
      </div>
      <ViewMoreSection>
        <div>
          <div className="text-gray-700 text-xs">{READY_QTY}</div>
          <div className="font-semibold text-sm text-gray-900 mt-[6px]">{item.jobReadyQty}</div>
        </div>
        <div>
          <div className="text-gray-700 text-xs">{DELIVERED_QTY}</div>
          <div className="font-semibold text-sm text-gray-900 mt-[6px]">{item.jobDeliveredQty}</div>
        </div>
        <div>
          <div className="text-gray-700">{DELIVERY_DOCKET}</div>
          {item.deliveryNumbers && item.deliveryNumbers.length > 0 ? (
            <div className="inline-flex flex-col gap-2 mt-2">
              {item.deliveryNumbers.map((singleItem, index) => (
                <Button
                  key={index}
                  icon={<img src={Images.IconDownload} alt="download" />}
                  className="text-orange-600 rounded-md border border-gray-300 cursor-pointer inline-flex justify-end items-start"
                  style={{
                    height: "32px",
                    color: THEME.COLORS.ORANGE_600
                  }}
                  onClick={() => {
                    // iOS only open view mode once trigger event directly inside button
                    if (isIOS || isIOS13 || isIPad13 || isTablet || isIPad) {
                      const printWindow = window.open("", "_blank");
                      if (!printWindow) {
                        showToast(ToastType.error, SOMETHING_WENT_WRONG);
                        return;
                      }
                      // Start fetching HTML content
                      usePrintDeliveryDocketIOS.mutate(singleItem, {
                        onSuccess: (data: any) => {
                          const parser = new DOMParser();
                          const doc = parser.parseFromString(data, "text/html");

                          // Format Delivery Date
                          const deliveryDateEl = doc.querySelector("#info .item .info-value");
                          if (deliveryDateEl?.textContent?.trim()) {
                            const deliveryDate = new Date(deliveryDateEl.textContent.trim());
                            const formatted = `${deliveryDate.getDate().toString().padStart(2, "0")}/${(deliveryDate.getMonth() + 1)
                              .toString()
                              .padStart(
                                2,
                                "0"
                              )}/${deliveryDate.getFullYear()} ${deliveryDate.getHours().toString().padStart(2, "0")}:${deliveryDate.getMinutes().toString().padStart(2, "0")}`;
                            deliveryDateEl.textContent = formatted;
                          }

                          // Format all Scan Time
                          const scanTimeCells = doc.querySelectorAll("#table3 tbody tr td:first-child");
                          scanTimeCells.forEach(cell => {
                            if (cell.textContent?.trim()) {
                              const scanTimeDate = new Date(cell.textContent.trim());
                              const formatted = `${scanTimeDate.getDate().toString().padStart(2, "0")}/${(scanTimeDate.getMonth() + 1)
                                .toString()
                                .padStart(
                                  2,
                                  "0"
                                )}/${scanTimeDate.getFullYear()} ${scanTimeDate.getHours().toString().padStart(2, "0")}:${scanTimeDate.getMinutes().toString().padStart(2, "0")}`;
                              cell.textContent = formatted;
                            }
                          });

                          const updatedHtml = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
                          printWindow.document.open();
                          printWindow.document.write(updatedHtml);
                          printWindow.document.close();
                        },
                        onError: () => {
                          printWindow.document.body.innerHTML = `<p>${SOMETHING_WENT_WRONG}</p>`;
                        }
                      });
                    } else {
                      handlePrintDeliveryDocket(singleItem);
                    }
                  }}
                >
                  {singleItem}
                </Button>
              ))}
            </div>
          ) : (
            <div className="font-semibold text-sm text-gray-900 mt-[6px]">{stringEmDash}</div>
          )}
        </div>
      </ViewMoreSection>

      {/* Footer Buttons */}
      <div className="w-full flex">
        <div className="flex-1 font-semibold text-black text-center py-2 rounded-bl-md bg-gray-200 ">{item.state}</div>
        <div
          className="flex-1 text-white text-center py-2 rounded-br-md"
          style={{ backgroundColor: statusColors[item.status ?? StatusId.inProduction] }}
        >
          {item.status}
        </div>
      </div>
    </div>
  );
};

export default ViewJobsListDetailItemCard;
